import {
    Component, OnInit, AfterViewInit, OnDestroy, Input,
    ViewChild, ElementRef, Renderer2, ViewEncapsulation, NgZone
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchEditionService } from '../../../services/search-edition.service';
import { CriticalEditionService } from '../../../services/critical-edition.service';
import { CeTooltipService } from '../../../services/ce-tooltip.service';
import { CeAppBoundList } from '../../../models/ce-app-bound';
import { CeSelection } from '../../../models/ce-selection';
import { Section, EDITION_SECTIONS } from '../../../models/search-edition-params';
import { SearchEditionResultRow, extractKwicInnerHtml, isTeiHeadKwic } from '../../../models/search-edition-results';
import { ISubscription } from 'rxjs/Subscription';

@Component({
  selector: 'app-ce-viewer',
  templateUrl: './ce-viewer.component.html',
  styleUrls: ['./ce-viewer.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class CeViewerComponent implements OnInit, AfterViewInit, OnDestroy {
    private subscriptions: Array<ISubscription> = [];
    private listeners: Array<any> = [];
    @Input() expandedImage: boolean;
    @Input() editionSample: boolean;
    @ViewChild('ceContent') ceContentRef: ElementRef;
    ceContentHTML: SafeHtml;
    ceAppBounds: CeAppBoundList;
    private appSelection: CeSelection;
    private contentSelection: CeSelection;
    private noteSelection: CeSelection;
    private biblioSelection: CeSelection;
    private divSelection: string;
    highlightSelection: any;
    highlightStartNode: any;
    appStartBounds: any;
    searchResult?: SearchEditionResultRow;
    private searchResultContainerHtml: string;
    editionSections: Array<Section> = EDITION_SECTIONS;

    constructor(
        private router: Router,
        private zone: NgZone,
        private activeRoute: ActivatedRoute,
        private renderer: Renderer2,
        private sanitizer: DomSanitizer,
        private criticalEditionService: CriticalEditionService,
        private tooltipService: CeTooltipService,
        private searchEditionService: SearchEditionService
    ) {
        this.tooltipService.setRenderer(this.renderer);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.activeRoute.params.subscribe(
                params => {
                    if (params['cedId']) {
                        const cedId = params['cedId'];
                        this.subscriptions.push(
                            this.criticalEditionService.getAppBounds(cedId, this.editionSample).subscribe(
                                response => this.ceAppBounds = response,
                                err => console.error(err)
                            ),
                            this.criticalEditionService.getContent(cedId, this.editionSample).subscribe(
                                response => this.ceContentHTML = this.sanitizer.bypassSecurityTrustHtml(response),
                                err => console.error(err)
                            ),
                            this.criticalEditionService.getAppSelection().subscribe(
                                response => this.updateAppSelection(response),
                                err => console.error(err)
                            ),
                            this.criticalEditionService.getContentSelection().subscribe(
                                response => this.updateContentSelection(response),
                                err => console.error(err)
                            ),
                            this.criticalEditionService.getNoteSelection().subscribe(
                                response => this.updateNoteSelection(response),
                                err => console.error(err)
                            ),
                            this.criticalEditionService.getBiblioSelection().subscribe(
                                response => this.updateBiblioSelection(response),
                                err => console.error(err)
                            ),
                            this.criticalEditionService.getDivSelection().subscribe(
                                response => this.updateDivSelection(response),
                                err => console.error(err)
                            ),
                            this.searchEditionService.getSearchEditionSelected().subscribe(
                                response => this.updateSearchResult(response),
                                err => console.error(err)
                            )
                        );
                    }
                },
                err => console.error(err)
            )
        );
    }

    ngAfterViewInit() {
        this.listeners.push(
            this.renderer.listen(this.ceContentRef.nativeElement, 'click', (event) => { this.onClick(event); }),
            this.renderer.listen(this.ceContentRef.nativeElement, 'mouseover', (event) => { this.onMouseOver(event); }),
            this.renderer.listen(this.ceContentRef.nativeElement, 'mouseout', (event) => { this.onMouseLeave(event); })
        );
    }

    onClick(event) {
        event.stopPropagation();

        /* Update critical apparatus selection */
        if (event.target.nodeName === 'A') {
            if (event.target.id.indexOf('app_') !== -1) { // apparatus links
                const appPortionBounds = this.ceAppBounds.find(
                    appBound => appBound.start === event.target.id
                );
                this.appSelection.from = appPortionBounds.start;
                this.appSelection.to = appPortionBounds.end;
                this.criticalEditionService.setAppSelection(this.appSelection);

                /* Restore highlighting after click */
                this.highlightRange(this.appSelection.from, this.appSelection.to);

            } else if (event.target.id.indexOf('nt_') !== -1) { // exegetical note links
                this.noteSelection.from = event.target.id;
                this.noteSelection.to = this.noteSelection.from;
                this.criticalEditionService.setNoteSelection(this.noteSelection);
            } else if (event.target.getAttribute('data-corresp').length > 0) { // translation links (div based)
                this.resetStartNodeStyle();
                this.divSelection = event.target.getAttribute('data-corresp');
                this.criticalEditionService.setDivSelection(this.divSelection);
            } else if (event.target.id.indexOf('bl_') !== -1) { // bibliographic links
                this.biblioSelection.from = event.target.id;
                this.biblioSelection.to = 'e-' + event.target.id;
                this.criticalEditionService.setBiblioSelection(this.biblioSelection);
            } else if (this.tooltipService.isReferenceLink(event.target.id)) {
                /* Show tooltip */
                this.tooltipService.showReferenceTooltip(event.target, event.target.id);
            }
        }

        /* Reference links */
        if (event.target.nodeName === 'DIV') {
            if (event.target.className.indexOf('ref_link_ms') !== -1) {
                this.zone.run(() => this.router.navigate(['/manuscripts/list/' + event.target.getAttribute('data-target')]));
            } else if (event.target.className.indexOf('ref_link_bib') !== -1) {
                this.zone.run(() => this.router.navigate(['/bibliography', {bibId: event.target.getAttribute('data-target')}]));
            }
        }

        /* Close reference button*/
        if (event.target.nodeName === 'I' && event.target.className.indexOf('rtt_close') !== -1) {
            this.tooltipService.removeReferenceTooltip();
        }
    }

    onMouseOver(event) {
        // event.stopPropagation();

        /* Anchor elements */
        if (event.target.nodeName === 'A' && event.target.id.indexOf('app_') !== -1) {
            this.highlightRange(
                event.target.id,
                this.ceAppBounds.find(
                    appBound => appBound.start === event.target.id
                ).end
            );
        } else if (event.target.id.indexOf('bl_') !== -1) { // bibliographic links
            this.highlightRange(
                event.target.id,
                'e-' + event.target.id
            );
        } else if (event.target.id.indexOf('ms_') !== -1 || event.target.id.indexOf('bib_') !== -1) { // general references
            this.highlightRange(
                event.target.id,
                'e-' + event.target.id
            );
        }
    }

    onMouseLeave(event) {
//        if (event.target.nodeName === 'A' && event.target.id.indexOf('app_') !== -1) {
//            if (this.highlightSelection.rangeCount > 0) {
//                this.highlightSelection.removeAllRanges();
//            }
//        }
    }

    updateAppSelection(appSelection: CeSelection) {
        this.appSelection = appSelection;
    }

    updateContentSelection(contentSelection: CeSelection) {
        this.contentSelection = contentSelection;
        this.highlightRange(this.contentSelection.from, this.contentSelection.to, true);
    }

    updateNoteSelection(noteSelection: CeSelection) {
        this.noteSelection = noteSelection;
    }

    updateBiblioSelection(biblioSelection: CeSelection) {
        this.biblioSelection = biblioSelection;
    }

    updateDivSelection(divSelection: string) {
        this.divSelection = divSelection;
    }

    updateSearchResult(searchResult?: SearchEditionResultRow) {
        /* Restore original content */
        if (!searchResult) {
            const searchResultContainer = this.getSearchResultContainerElement();
            if (searchResultContainer && this.searchResultContainerHtml) {
                searchResultContainer.innerHTML = this.searchResultContainerHtml;
                this.searchResultContainerHtml = null;
            }
        }

        this.searchResult = searchResult;
    }

    highlightRange(startNodeId: string, endNodeId: string, scrollToSelect: boolean = false) {
        if (startNodeId && endNodeId) {
            const appStartNode = this.ceContentRef.nativeElement.querySelector('[id="' + startNodeId + '"]');
            const appEndNode = this.ceContentRef.nativeElement.querySelector('[id="' + endNodeId + '"]');

            if (window.getSelection && document.createRange) {
                this.highlightSelection = window.getSelection();

                /* Remove previous selection */
                if (this.highlightSelection.rangeCount > 0) {
                    this.highlightSelection.removeAllRanges();
                }
                /* Remove styles from selection start node */
                this.resetStartNodeStyle();

                if (appStartNode && appEndNode) {
                    /* Selection range anchors */
                    const highlightRange = document.createRange();
                    highlightRange.setStartAfter(appStartNode);
                    highlightRange.setEndBefore(appEndNode);

                    /* Highlight selection */
                    this.highlightSelection.addRange(highlightRange);

                    /* Highlight selection start node */
                    this.highlightStartNode = appStartNode;
                    appStartNode.style.color = '#6A93A4';
                    appStartNode.style.background = '#FFDAB9';

                    /* Scroll to selection start */
                    if (scrollToSelect) {
                        // appStartNode.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
                        // -- scroll-behavior moved to css
                        appStartNode.scrollIntoView();
                    }
                }
            }
        }
    }

    private resetStartNodeStyle(): void {
        if (this.highlightStartNode) {
            this.highlightStartNode.style.color = '#C9302C';
            this.highlightStartNode.style.background = '';
        }
    }

    viewSearchResult() {
        if (this.searchResult) {
            switch (this.searchResult.section) {
                case 'body':
                    const searchResultContainer = this.getSearchResultContainerElement();
                    /* Search result highlighting */
                    if (searchResultContainer && !this.searchResultContainerHtml) {
                        this.searchResultContainerHtml = searchResultContainer.innerHTML;
                        searchResultContainer.innerHTML = extractKwicInnerHtml(this.searchResult.kwic);
                        searchResultContainer.scrollIntoView();
                    }
                    break;
                case 'listApp':
                    this.appSelection.id = this.searchResult.id;
                    this.criticalEditionService.setAppSelection(this.appSelection);
                    break;
                case 'notes':
                    this.noteSelection.from = this.searchResult.id;
                    this.noteSelection.to = this.noteSelection.from;
                    this.criticalEditionService.setNoteSelection(this.noteSelection);
                    break;
                case 'trans_en':
                    this.divSelection = this.searchResult.id;
                    this.criticalEditionService.setDivSelection('en-' + this.divSelection);
                    break;
                case 'trans_la':
                    this.divSelection = this.searchResult.id;
                    this.criticalEditionService.setDivSelection('la-' + this.divSelection);
                    break;
            }
        }
    }

    backToSearchResults() {
        this.zone.run(() => this.router.navigate(['/edition/search']));
    }

    removeSearchResult() {
        this.searchEditionService.resetSearchEditionSelected();
    }

    private getSearchResultContainerElement(): any|null {
        if (this.searchResult && this.searchResult.section === 'body') {
            if (isTeiHeadKwic(this.searchResult.kwic)) {
                return this.ceContentRef.nativeElement.querySelector('[xml\\:id="' + this.searchResult.id + '"]>data-head');
            } else {
                return this.ceContentRef.nativeElement.querySelector('[xml\\:id="' + this.searchResult.id + '"]');
            }
        }
        return null;
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
        for (const listener of this.listeners) {
            if (listener) {
                listener();
            }
        }
        this.tooltipService.setRenderer(null);
    }
}



// WEBPACK FOOTER //
// ./src/app/components/ui/ce-viewer/ce-viewer.component.ts